import BaseService from '../../base/base_service';
import _ from '../../utils/underscore'; // eslint-disable-line id-length
import Accounting from 'accounting';
import {SYSTEM_EVENTS} from '../../events/events';


import {
  CURRENCY_SYMBOLS,
  CURRENCY_FORMAT_SYMBOL_SELECTOR,
  CURRENCY_DEFAULT_FORMAT,
  CURRENCY_FORMAT_NAME,
  WALLET_TOKEN_PROPERTY_KEY
} from './currency_consts';
import ICurrencyService, {
    CurrencyServiceInitParams,
    ICurrencyFormattingOptions,
    IFormatStructure
} from './currency_service_interface';


class CurrencyService extends BaseService implements ICurrencyService{
  /**
   * @static
   * @type {string}
   */
  static CLASS: string = 'CurrencyService';

  private _initParams: CurrencyServiceInitParams;
  private _currencyFormat: string;


  init(params: CurrencyServiceInitParams) {
    super.init(params);
    this._initParams = params;
    this._subscribe();
  }

  public getCurrency(): string {
    return this._initParams.currency;
  }

  public getAnonymousCurrency(): string {
    return this._initParams.anonymousCurrency;
  }

  public getCurrencySymbol(countryCode?: string): string {
    return CURRENCY_SYMBOLS[countryCode || this._initParams.currency];
  }

  /**
   * Format money due to formatting rules.
   *
   * @return {string}
   */
  public formatMoney(value: number, options: ICurrencyFormattingOptions = {}, roundIfZero: boolean = false): string {

    if (!_.isNumber(value)) {
      // Set default value to zero.
      value = 0;
    }

    //If the numbers after the decimal point are 0/00 we will return the number without decimal point if roundIfZero = true
    if(roundIfZero && (value / 100) % 1 === 0) {
      options.precision = 0;
    }

    // Formatting money.
    return Accounting.formatMoney(value / 100, this._modifyAccountingLibraryFunctionality(this._currencyFormat, options));
  }

  private _subscribe(): void {
    this.onSystemEvent([SYSTEM_EVENTS.ON_INIT_COMPLETE, SYSTEM_EVENTS.CURRENCY_UPDATED, SYSTEM_EVENTS.OAUTH_STATE_CHANGE], () => {
      this._initParams.currency = this._currencyCode();
      this._refreshCurrencyFormat();
    });
  }

  /**
   * Create formatting with the format and currency.
   *
   * @param structureFormat
   * @returns {string|void|XML|*}
   * @private
   */
  private _createFormatting(structureFormat: IFormatStructure): string {
    const regulationCurrencySymbol = this.serviceFactory.regulationService().getRegulation().overrideCurrencySymbol();
    const currSymbol = regulationCurrencySymbol || this.getCurrencySymbol(structureFormat.currency) || structureFormat.currency;

    if (_.isUndefined(currSymbol)) {
      this.logger.error('Currency formatting not set properly => ', this._initParams);
      throw new Error('');
    }

    if (_.isUndefined(structureFormat.format)) {
      this.logger.info('No format was set. Default format will be used', CURRENCY_DEFAULT_FORMAT);

      structureFormat.format = CURRENCY_DEFAULT_FORMAT;
    }

    return structureFormat.format.replace(CURRENCY_FORMAT_SYMBOL_SELECTOR, currSymbol);
  }


  /**
   * Get the current currency code. when entering this func we already called to '_setAnonymousCurrency'
   * so we have default anonymous currency.
   *
   * @returns {*}
   * @private
   */
  private _currencyCode(): string {
    const anonymousCurrency = this.getAnonymousCurrency().toUpperCase();

    // NOTE: For some reason accounting need only upper case currency lol :)..
    if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
      // this.logger.debug('User not authorized, using default currency code: '+ anonymousCurrency);
      return anonymousCurrency;
    }

    const tokenCurrency = this._localStorageTokenCurrency();

    return (tokenCurrency ? tokenCurrency : anonymousCurrency).toUpperCase();
  }


  /**
   * Get local storage currency value from token if exist.
   *
   * @returns {*}
   * @private
   */
  private _localStorageTokenCurrency(): string {
    // Get token from local storage.
    const walletToken = _.safeJSON(this.serviceFactory.oAuth2Service().extractTokenProperty(WALLET_TOKEN_PROPERTY_KEY));
    if(!walletToken) {
      this.logger.error(`WalletToken fail parsing. walletToken ${this.serviceFactory.oAuth2Service().extractTokenProperty(WALLET_TOKEN_PROPERTY_KEY)}`);
      return null;
    } else {
      return walletToken.CurrencyCode;
    }
  }

  private _languageFormatting(): string {
    return this.serviceFactory.languageService().translation(CURRENCY_FORMAT_NAME);
  }


  /**
   * Our modify the functionality of the accounting js external library.
   *
   * @param currencyFormat
   * @param options
   * @returns {{symbol: undefined, precision: undefined, thousand: undefined, decimal: undefined, format: undefined}}
   * @private
   */
  private _modifyAccountingLibraryFunctionality(currencyFormat: string, options: ICurrencyFormattingOptions = {}) {
    const splitFormat = currencyFormat.split('{#}');
    const length = splitFormat.length;
    const fullFormat = {
        symbol: length > 0 ? splitFormat[0] : undefined,
        precision: length > 1 ? options.precision || parseInt(splitFormat[1]) : undefined,
        thousand: length > 2 ? splitFormat[2] : undefined,
        decimal: length > 3 ? splitFormat[3] : undefined,
        format: length > 4 ? splitFormat[4] : undefined
      };

    return Object.assign(fullFormat, options);
  }

  /**
   * Refresh currency format set new or same due to change currency model format,
   * The currency format.
   *
   * @private
   */
  private _refreshCurrencyFormat(): void {
    const structureFormat: IFormatStructure = {
      currency: this._initParams.currency,
      format: this._initParams.format
    };
    this._currencyFormat = this._createFormatting(structureFormat);
  }

  public doAsyncJobs(): Promise<any> {
    return this._asyncAnonymousCurrency();
  }

  /**
   * Actions after getting anonymous currency
   *
   * @private
   */
  private _anonymousCurrencyPostActions(anonymousCurrency: string): void {
    try {
      // set the format
      this._initParams.format = this._languageFormatting();
      // this will define the currency model at first time and set anonymous currency code
      this._initParams.anonymousCurrency = anonymousCurrency;
      // set currency to be anonymous at first time before user log in
      this._initParams.currency = anonymousCurrency;
      this._refreshCurrencyFormat();
    } catch(err) {
      this.logger.error('Fail in _anonymousCurrencyPostActions method.', err);
    }

  }


  /**
   * Get the anonymous currency code
   *
   * @returns {*}
   * @private
   */
  private _asyncAnonymousCurrency(): Promise<void> {
    return new Promise((resolve) => {
      const countryService = this.serviceFactory.countryService();
      const country = countryService.getCountry();
      const defaultCountry = countryService.getDefaultCountry();
      const countryCurrencyMap = this._initParams.countryCurrencyMap;
      // if the country is not valid, use the default
      const anonymousCurrency = this.getAnonymousCurrency() || countryCurrencyMap[country] || countryCurrencyMap[defaultCountry];

      this._anonymousCurrencyPostActions(anonymousCurrency);
      resolve();
    });
  }
}

export default CurrencyService;